"use client";

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const HOVER_QUERY = "(hover: hover) and (pointer: fine)";

const HoverCapableContext = createContext<boolean | null>(null);

function useHoverQuery(enabled: boolean) {
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(HOVER_QUERY);
    const update = () => setCanHover(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, [enabled]);

  return canHover;
}

/**
 * Answers the hover question once for the whole tree. The point is
 * consistency, not cost: `lib/touch.ts` deliberately asks a *different*
 * question (`pointer: coarse`, the machine's primary pointer), and with two
 * dozen callers the two are one careless import apart.
 *
 * Built with `createElement` so this stays a `.ts` module next to the hook it
 * serves.
 */
export function HoverCapableProvider({ children }: { children: ReactNode }) {
  const canHover = useHoverQuery(true);
  return createElement(
    HoverCapableContext.Provider,
    { value: canHover },
    children,
  );
}

/**
 * Returns true only on devices that have a true hover (mouse / trackpad).
 * Touch devices fire phantom `:hover` on tap that sticks until tap-elsewhere
 * — gate hover-only effects (scale lifts, magnetic pulls) behind this.
 *
 * Falls back to its own media query where no provider is mounted, so the hook
 * still works on its own.
 */
export function useHoverCapable() {
  const provided = useContext(HoverCapableContext);
  const fallback = useHoverQuery(provided === null);
  return provided ?? fallback;
}
